import { useEffect } from 'react';
import { observer } from 'mobx-react';
import {
  Space,
  Button,
  Skeleton,
  Tooltip,
  Typography,
  Alert,
} from 'antd';
import { HeartOutlined, ReloadOutlined } from '@ant-design/icons';
import type { HealthStatus, HealthCheck } from '@/api/systemHealth';
import { HEALTH_STATUS_META as STATUS_META } from './healthStatus';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// System Health page (v1)
// ─────────────────────────────────────────────────────────────────────────────
//
// Live dependency check for the platform. Polls /api/SystemHealth/Live every
// 30 seconds through SystemHealthStore (the one poller, shared with the right
// rail's health dot); user can also click Refresh. Shows one card per component
// with status (ok / degraded / down / unknown), latency, last-checked, and
// the detail string from the server.
//
// The "ships in Sprint 7" placeholder still owns the unchecked features
// (recent incidents, latency percentiles, alert subscriptions) — those
// require App Insights KQL on the server side and will land in a follow-up.
// ─────────────────────────────────────────────────────────────────────────────

const REFRESH_INTERVAL_MS = 30_000;

export const SystemHealthPage = observer(function SystemHealthPage() {
  const { themeStore, systemHealthStore: health } = useStore();
  const isDark = themeStore.mode === 'dark';
  const { summary, loading, error } = health;
  const refresh = () => void health.refresh();

  // Join the shared poller (SystemHealthStore — the right rail's health dot
  // reads the same answers) at this page's 30 s cadence while it is open.
  useEffect(() => health.subscribe(REFRESH_INTERVAL_MS), [health]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="green"
        icon={<HeartOutlined />}
        title="System Health"
        subtitle="Live dependency status. Polls every 30 seconds in the background."
        actions={
          <Space>
            {summary && <OverallBadge status={summary.overall} />}
            <Button icon={<ReloadOutlined />} loading={loading && !summary} onClick={refresh}>
              Refresh
            </Button>
          </Space>
        }
      />

      {error && <Alert type="error" showIcon message="Health check failed" description={error} closable />}

      {!summary && loading ? (
        <SectionCard>
          <Skeleton active paragraph={{ rows: 4 }} />
        </SectionCard>
      ) : summary ? (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 12,
            }}
          >
            {summary.components.map((c) => (
              <ComponentCard key={c.component} check={c} isDark={isDark} />
            ))}
          </div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
            Last refresh: {new Date(summary.generatedAtUtc).toLocaleTimeString()} · auto-refreshes every {REFRESH_INTERVAL_MS / 1000}s
          </Text>
        </>
      ) : null}

      {/* Roadmap — what's still to come on this surface. */}
      <SectionCard
        title="On the roadmap"
        subtitle="Surfaces still to land on this page; backend pieces are in flight."
      >
        <ul
          style={{
            margin: 0,
            paddingLeft: 20,
            color: isDark ? colors.slate[300] : colors.slate[600],
            fontSize: 13.5,
            lineHeight: 1.8,
          }}
        >
          <li><b>Recent incidents</b> — last 10 status changes from App Insights traces.</li>
          <li><b>Latency percentiles</b> — P50 / P95 / P99 for token requests, scenario runs, KB queries.</li>
          <li><b>Alert subscriptions</b> — Slack / Teams / email when a dependency drops to <code>down</code>.</li>
          <li><b>Foundry runtime check</b> — explicit ping against the Azure AI Foundry endpoint.</li>
          <li><b>Cosmos DB / Azure AI Search checks</b> — once we host them as first-class deps.</li>
        </ul>
      </SectionCard>
    </div>
  );
});

// ── Per-component card ─────────────────────────────────────────────────────

function ComponentCard({ check, isDark }: { check: HealthCheck; isDark: boolean }) {
  const meta = STATUS_META[check.status];
  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderLeft: `3px solid ${meta.color}`,
        borderRadius: 10,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: meta.color, fontSize: 18 }}>{meta.icon}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: isDark ? colors.slate[50] : colors.slate[900],
            }}
          >
            {check.component}
          </div>
          <Text type="secondary" style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {meta.label}
          </Text>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 13,
              fontWeight: 600,
              color: isDark ? colors.slate[100] : colors.slate[800],
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {check.latencyMs}ms
          </div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            <Tooltip title={`Checked at ${new Date(check.checkedAtUtc).toLocaleString()}`}>
              {humanRelative(new Date(check.checkedAtUtc))}
            </Tooltip>
          </Text>
        </div>
      </div>
      {check.detail && (
        <div
          style={{
            fontSize: 12.5,
            lineHeight: 1.5,
            color: isDark ? colors.slate[300] : colors.slate[600],
            background: isDark ? colors.slate[900] : colors.slate[50],
            border: `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`,
            borderRadius: 6,
            padding: '6px 10px',
            wordBreak: 'break-word',
          }}
        >
          {check.detail}
        </div>
      )}
    </div>
  );
}

function OverallBadge({ status }: { status: HealthStatus }) {
  const meta = STATUS_META[status];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: `${meta.color}1A`,
        color: meta.color,
      }}
    >
      {meta.icon} {meta.label}
    </span>
  );
}

function humanRelative(when: Date): string {
  const diffSec = Math.max(0, Math.floor((Date.now() - when.getTime()) / 1000));
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  return when.toLocaleTimeString();
}
